import { useState, type ReactNode } from 'react';
import { WarningIcon } from '@phosphor-icons/react';
import { Checkbox } from '@voll-intelligence/ui-kit/ui/checkbox';
import type { FlightAirportSwitchView } from '../results/offer/offer_copy.js';
import type { FlightFaresCopy } from './fares_copy.js';
import type { FaresPageFlight } from './fares_page_shared.js';

/**
 * **O portão da volta de outro aeroporto (#4203)** — o último lugar antes de
 * cotar/reservar. Quando a volta parte de um aeroporto diferente daquele em que
 * a ida chega (e a pessoa NÃO pediu a cidade inteira, `expected`), a tela diz
 * os dois aeroportos e o CTA só libera com a ciência marcada. A ciência vale
 * para ESTE par de voos: trocar uma das pernas pede de novo. Quem decide se há
 * desvio é o host (`found`), que conhece o pedido.
 */
export function useAirportSwitchGate(
  found: FlightAirportSwitchView | null | undefined,
  outbound: FaresPageFlight,
  inbound: FaresPageFlight | undefined,
  readOnly: boolean | undefined,
  copy: FlightFaresCopy,
): { notice: ReactNode; locked: boolean } {
  const [ackedPair, setAckedPair] = useState<string | null>(null);
  if (!found || found.expected || !inbound) return { notice: null, locked: false };
  const pair = `${outbound.offerId}|${inbound.offerId}`;
  const acked = ackedPair === pair;
  const notice = (
    <div role="alert" className="grid gap-2 rounded-lg bg-warning-soft p-3 text-ui text-foreground">
      <p className="m-0 flex items-start gap-1.5 font-medium">
        <WarningIcon size={16} className="mt-0.5 shrink-0 text-warning-strong" aria-hidden />
        {copy.airportSwitch.title}
      </p>
      <p className="m-0">{copy.airportSwitch.body(found.arrival, found.departure)}</p>
      <label className="flex items-center gap-2">
        <Checkbox
          checked={acked}
          disabled={Boolean(readOnly)}
          onCheckedChange={(checked) => {
            setAckedPair(checked ? pair : null);
          }}
        />
        {copy.airportSwitch.ack}
      </label>
    </div>
  );
  return { notice, locked: !acked };
}
